import React, { useEffect, useState, useMemo } from "react";
import { useSetRecoilState, useRecoilValue } from "recoil";
import { ReflexContainer, ReflexElement, ReflexSplitter } from "react-reflex";
import { debounce } from "lodash";
import layoutConf from "../../config/layoutConf";

import authProfileState from "../../states/authProfileState";
import notificationState from "../../states/notificationState";
import defineConf from "../../config/defineConf";
import AccessControl from "../functional/AccessControl";

import { useTranslation } from "react-i18next";

import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import BasicCrudScreenActionItemLayout from "../screenTypes/layout/BasicCrudScreenActionItemLayout";
import TkPartnerPriceAgreementFile from "./TkPartnerPriceAgreementFile";
import getSchema from "../../lib/api/getSchema";
import TkPartnerPriceAgreementUpdateButton from "../input/TkPartnerPriceAgreementUpdateButton";
import TkPartnerPriceAgreementRsltUpdateButton from "../input/TkPartnerPriceAgreementRsltUpdateButton";
import TkPartnerPriceAgreementConfirmButton from "../input/TkPartnerPriceAgreementConfirmButton";
import YearMonthDateRangeSelector from "../input/YearMonthDateRangeSelector";

const MomSysUglOrderBuyerDetailFiles = (props) => {
    const { orderBuyerDetailId } = props;

    const id = "MomSysUglOrderBuyerDetailFiles";
    const view = "MomSysZvwOrderBuyerDetailFiles";

    const { t } = useTranslation();

    const [orderBuyerDetailFilesApi, setOrderBuyerDetailFilesApi] = useState();

    useEffect(() => {
        if (orderBuyerDetailFilesApi) {
            orderBuyerDetailFilesApi.refresh();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderBuyerDetailId]);

    return (
        <BasicCrudScreenType
            id={id}
            headerTitle={t(`term.${id}`)}
            view={view}
            onInitialized={(grid) => {
                grid.selectionMode = "None";
            }}
            onApiInitialized={(api) => {
                setOrderBuyerDetailFilesApi(api);
            }}
            onModifyViewUrl={(url) => `${url}/${orderBuyerDetailId}`}
            noEdit
            noExcel
            noFilter
            noPagination
        />
    );
};

function TkPartnerPriceAgreement(props) {
    const id = "TkPartnerPriceAgreement";
    const table = "TkPartnerPriceAgreement";
    const view = "TkVwPartnerPriceAgreement";

    const { t } = useTranslation();

    const [height, setHeight] = useState(0);

    const [tkPartnerPriceAgreementSchema, setTkPartnerPriceAgreementSchema] = useState();
    const [tkPartnerPriceAgreementGrid, setTkPartnerPriceAgreementGrid] = useState();
    const [tkPartnerPriceAgreementIsEmpty, setTkPartnerPriceAgreementDataIsEmpty] = useState();
    const [tkPartnerPriceAgreementApi, setTkPartnerPriceAgreementApi] = useState();
    const [tkPartnerPriceAgreementSelect, setTkPartnerPriceAgreementSelect] = useState();
    const [tkPartnerPriceAgreementSelectArr, setTkPartnerPriceAgreementSelectArr] = useState();

    const [viewCurrDate] = useState(new Date());
    const [viewFromDate, setViewFromDate] = useState();
    const [viewToDate, setViewToDate] = useState();

    const authProfile = useRecoilValue(authProfileState);

    const setNotification = useSetRecoilState(notificationState);

    const handleResize = () => {
        setHeight(document.documentElement.offsetHeight - 96);
    };

    const modifyPartnerTableSchema = (schema) => {
        if (authProfile.companyInfo?.typeSupplyer || authProfile.companyInfo?.typeOutSourcing) {
            // hide price information
            schema.columns.forEach((column) => {
                switch (column.name) {
                    case "KG":
                    case "materialPrice":
                    case "facingPrice":
                    case "polishingPrice":
                    case "THomCost":
                    case "processingUnitPrice":
                    case "totalPrice":
                        column.hideInForm = true;

                        break;

                    default:
                        break;
                }
            });
        }
    };

    const modifyPartnerViewSchema = (schema) => {
        if (authProfile.companyInfo?.typeSupplyer || authProfile.companyInfo?.typeOutSourcing) {
            // hide price information
            schema.columns.forEach((column) => {
                switch (column.name) {
                    case "KG":
                    case "materialPrice":
                    case "facingPrice":
                    case "polishingPrice":
                    case "THomCost":
                    case "processingUnitPrice":
                    case "totalPrice":
                        column.grid.hide = true;

                        break;

                    default:
                        break;
                }
            });
        }
    };

    useEffect(() => {
        let mounted = true;

        window.addEventListener("resize", handleResize);
        handleResize();

        (async () => {
            let schema = await getSchema(table);

            modifyPartnerTableSchema(schema);

            if (mounted) {
                setTkPartnerPriceAgreementSchema(schema);
            }
        })();

        return () => {
            mounted = false;
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        let mounted = true;

        (async () => {
            if (tkPartnerPriceAgreementGrid && mounted) {
                tkPartnerPriceAgreementGrid.selectionChanged.addHandler(
                    debounce((grid, event) => {
                        if (grid.selectedItems && grid.selectedItems.length > 0) {
                            setTkPartnerPriceAgreementSelect(grid.selectedItems[0]);
                            setTkPartnerPriceAgreementSelectArr(grid.selectedItems);
                        } else {
                            setTkPartnerPriceAgreementSelect();
                            setTkPartnerPriceAgreementSelectArr();
                        }
                    }),
                    10
                );

                tkPartnerPriceAgreementGrid.itemsSourceChanged.addHandler(
                    debounce((grid, event) => {
                        if (grid.selectedItems && grid.selectedItems.length > 0) {
                            setTkPartnerPriceAgreementSelect(grid.selectedItems[0]);
                            setTkPartnerPriceAgreementSelectArr(grid.selectedItems);
                        } else {
                            setTkPartnerPriceAgreementSelect();
                            setTkPartnerPriceAgreementSelectArr();
                        }
                    }),
                    10
                );
            }
        })();

        return () => {
            mounted = false;
        };
    }, [tkPartnerPriceAgreementGrid]);

    const notifySuccess = (msg) => {
        setNotification({
            severity: "success",
            msg: msg,
            open: true,
        });
    };

    const notifyFailure = (msg) => {
        setNotification({
            severity: "error",
            msg: msg,
            open: true,
        });
    };

    const notifyWarning = (msg) => {
        setNotification({
            severity: "warning",
            msg: msg,
            open: true,
        });
    };

    const onSuccess = (msg) => {
        notifySuccess(msg);
    };

    const onFailure = (msg) => {
        notifyFailure(msg);
    };

    const onPartialFailure = (msg) => {
        notifyWarning(msg);
    };

    const onViewDateRangeChanged = (from, to) => {
        setViewFromDate(from);
        setViewToDate(to);
    };

    const toYearMonth = useMemo(() => {
        return new Date(viewCurrDate.getFullYear(), viewCurrDate.getMonth());
    }, [viewCurrDate]);

    const fromYearMonth = useMemo(() => {
        return new Date(viewCurrDate.getFullYear(), viewCurrDate.getMonth() - 1);
    }, [viewCurrDate]);

    const queries = useMemo(() => {
        return { $from: viewFromDate, $to: viewToDate };
    }, [viewFromDate, viewToDate]);

    return (
        <div style={{ height: height }}>
            <ReflexContainer orientation="horizontal">
                <ReflexElement flex={0.7}>
                    <BasicCrudScreenType
                        id={id}
                        view={view}
                        headerTitle={t("term.partnerPriceAgreement")}
                        onInitialized={(grid) => {
                            grid.selectionMode = "Row";
                            setTkPartnerPriceAgreementGrid(grid);
                        }}
                        onApiInitialized={(api) => {
                            setTkPartnerPriceAgreementApi(api);
                        }}
                        onLoadData={(data) => {
                            setTkPartnerPriceAgreementDataIsEmpty(data.length < 1 ? true : false);
                        }}
                        onModifyViewSchema={(schema) => {
                            modifyPartnerViewSchema(schema);
                        }}
                        onModifyViewUrl={(url) => {
                            if (authProfile.companyInfo?.typeOwn) {
                                return url;
                            } else if (
                                authProfile.companyInfo?.typeSupplyer ||
                                authProfile.companyInfo?.typeOutSourcing
                            ) {
                                return `${url}/${authProfile.userCompanyCode}`;
                            }

                            return url;
                        }}
                        noExcel
                        $queries={queries}
                        onAppendButtonAtFirst={() => (
                            <React.Fragment>
                                <YearMonthDateRangeSelector
                                    onChange={onViewDateRangeChanged}
                                    initFrom={fromYearMonth}
                                    initTo={toYearMonth}
                                />
                            </React.Fragment>
                        )}
                        onAppendButtonInFrontOfSearch={() => (
                            <BasicCrudScreenActionItemLayout>
                                <AccessControl
                                    accessTarget={tkPartnerPriceAgreementSchema && tkPartnerPriceAgreementSchema.name}
                                    accessMethod={
                                        tkPartnerPriceAgreementSchema &&
                                        (tkPartnerPriceAgreementSchema.updateWithPatch ? "PATCH" : "PUT")
                                    }
                                >
                                    <TkPartnerPriceAgreementUpdateButton
                                        id={id}
                                        schema={tkPartnerPriceAgreementSchema}
                                        grid={tkPartnerPriceAgreementGrid}
                                        priceAgreementSelect={tkPartnerPriceAgreementSelect}
                                        refresh={tkPartnerPriceAgreementApi}
                                        disabled={
                                            !tkPartnerPriceAgreementSelect ||
                                            tkPartnerPriceAgreementSelect.ourAgreementCompleted ===
                                                defineConf.agreementRslt.AGREEMENT_COMPLETED ||
                                            tkPartnerPriceAgreementSelect.partnerAgreementCompleted ===
                                                defineConf.agreementRslt.AGREEMENT_COMPLETED
                                        }
                                        onSuccess={onSuccess}
                                    />
                                </AccessControl>

                                {authProfile.companyInfo?.typeOwn ? (
                                    <AccessControl
                                        accessTarget={
                                            tkPartnerPriceAgreementSchema && tkPartnerPriceAgreementSchema.name
                                        }
                                        accessMethod={
                                            tkPartnerPriceAgreementSchema &&
                                            (tkPartnerPriceAgreementSchema.updateWithPatch ? "PATCH" : "PUT")
                                        }
                                    >
                                        <TkPartnerPriceAgreementConfirmButton
                                            id={id}
                                            schema={tkPartnerPriceAgreementSchema}
                                            grid={tkPartnerPriceAgreementGrid}
                                            refresh={tkPartnerPriceAgreementApi}
                                            confirmType={defineConf.priceAgreementConfirmType.OUR_CONFIRM}
                                            disabled={
                                                !tkPartnerPriceAgreementSelectArr ||
                                                tkPartnerPriceAgreementSelectArr.length === 0 ||
                                                tkPartnerPriceAgreementSelectArr.some(
                                                    (v) =>
                                                        v.partnerAgreementCompleted !==
                                                        defineConf.agreementRslt.AGREEMENT_COMPLETED
                                                ) ||
                                                tkPartnerPriceAgreementSelectArr.every(
                                                    (v) =>
                                                        v.ourAgreementCompleted ===
                                                        defineConf.agreementRslt.AGREEMENT_COMPLETED
                                                )
                                            }
                                            onSuccess={onSuccess}
                                            onFailure={onFailure}
                                            onPartialFailure={onPartialFailure}
                                        />
                                    </AccessControl>
                                ) : (
                                    <></>
                                )}

                                {authProfile.companyInfo?.typeSupplyer || authProfile.companyInfo?.typeOutSourcing ? (
                                    <AccessControl
                                        accessTarget={
                                            tkPartnerPriceAgreementSchema && tkPartnerPriceAgreementSchema.name
                                        }
                                        accessMethod={
                                            tkPartnerPriceAgreementSchema &&
                                            (tkPartnerPriceAgreementSchema.updateWithPatch ? "PATCH" : "PUT")
                                        }
                                    >
                                        {/*** 2023/01/17 itkim : 
                                         * partnerAgreementCompleted 칼럼의 값이 boolean에서 여러개로 변하며서 
                                         * TkPartnerPriceAgreementRsltUpdateButton 으로 대체
                                        <TkPartnerPriceAgreementConfirmButton
                                            id={id}
                                            schema={tkPartnerPriceAgreementSchema}
                                            grid={tkPartnerPriceAgreementGrid}
                                            refresh={tkPartnerPriceAgreementApi}
                                            confirmType={defineConf.priceAgreementConfirmType.PARTNER_CONFIRM}
                                            disabled={
                                                !tkPartnerPriceAgreementSelectArr ||
                                                tkPartnerPriceAgreementSelectArr.length === 0 ||
                                                tkPartnerPriceAgreementSelectArr.some((v) => {
                                                    if (
                                                        v.agreementType ===
                                                        defineConf.agreementType.ORDER_PURCHASE_PRICE_AGREEMENT
                                                    ) {
                                                        if (!v.partnerPrice) {
                                                            return true;
                                                        }
                                                    } else if (
                                                        v.agreementType ===
                                                        defineConf.agreementType.OUTSOURCING_PRICE_AGREEMENT
                                                    ) {
                                                        if (!v.processTimeHour) {
                                                            return true;
                                                        }
                                                    }

                                                    return false;
                                                }) ||
                                                tkPartnerPriceAgreementSelectArr.every((v) =>
                                                    v.partnerAgreementCompleted ? true : false
                                                )
                                            }
                                            onSuccess={onSuccess}
                                            onFailure={onFailure}
                                            onPartialFailure={onPartialFailure}
                                        />
                                        ***/}

                                        <TkPartnerPriceAgreementRsltUpdateButton
                                            id={id}
                                            schema={tkPartnerPriceAgreementSchema}
                                            grid={tkPartnerPriceAgreementGrid}
                                            priceAgreementSelect={tkPartnerPriceAgreementSelect}
                                            refresh={tkPartnerPriceAgreementApi}
                                            disabled={
                                                !tkPartnerPriceAgreementSelect ||
                                                (tkPartnerPriceAgreementSelect.ourAgreementCompleted ===
                                                    defineConf.agreementRslt.AGREEMENT_COMPLETED &&
                                                    tkPartnerPriceAgreementSelect.partnerAgreementCompleted ===
                                                        defineConf.agreementRslt.AGREEMENT_COMPLETED)
                                            }
                                            onSuccess={onSuccess}
                                        />
                                    </AccessControl>
                                ) : (
                                    <></>
                                )}
                            </BasicCrudScreenActionItemLayout>
                        )}
                    />
                </ReflexElement>

                <ReflexSplitter style={{ height: layoutConf.reflex.splitterHeight }} />

                <ReflexElement flex={0.3}>
                    <ReflexContainer orientation="vertical">
                        <ReflexElement flex={0.5}>
                            {tkPartnerPriceAgreementSelect ? (
                                <MomSysUglOrderBuyerDetailFiles
                                    orderBuyerDetailId={tkPartnerPriceAgreementSelect.orderBuyerDetailId}
                                />
                            ) : (
                                <></>
                            )}
                        </ReflexElement>

                        <ReflexSplitter style={{ width: layoutConf.reflex.splitterWidth }} />

                        <ReflexElement flex={0.5}>
                            {tkPartnerPriceAgreementSelect ? (
                                <TkPartnerPriceAgreementFile onSelectMst={tkPartnerPriceAgreementSelect} />
                            ) : (
                                <></>
                            )}
                        </ReflexElement>
                    </ReflexContainer>
                </ReflexElement>
            </ReflexContainer>
        </div>
    );
}

export default TkPartnerPriceAgreement;
